<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import ModalAdditionalInformation from "@/components/administration/ModalAdditionalInformation.vue";
import programPatientService from "@/services/programPatient";
import {
  getProgramStatusType,
  getProgramStatusText,
} from "@/services/program-status";
import programPatientStatusMixin from "@/mixins/programPatientStatus";

export default {
  name: "Administration",
  components: {
    BaseSubNav,
    Spinner,
    ModalAdditionalInformation,
  },
  mixins: [programPatientStatusMixin],
  data() {
    return {
      isLoading: false,
      programPatients: [],
      searchInput: "",
      showAdditionalInfoModal: false,
      selectedProgramPatient: null,
    };
  },
  computed: {
    filteredProgramPatients() {
      if (!this.searchInput) return this.programPatients;

      const query = this.searchInput.toLowerCase();
      return this.programPatients.filter((pp) => {
        const patient = pp.patient;
        const program = pp.program;
        return (
          patient?.firstName?.toLowerCase().includes(query) ||
          patient?.lastName?.toLowerCase().includes(query) ||
          program?.name?.toLowerCase().includes(query)
        );
      });
    },
  },
  watch: {
    "$route.name": {
      handler: function () {
        this.getData();
      },
      immediate: true,
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const res = await programPatientService.getProgramPatients({
          status: "pending",
        });
        this.programPatients = res.data || res;
      } catch (error) {
        console.error("Error loading program patients:", error);
      } finally {
        this.isLoading = false;
      }
    },
    activateProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Aprobar programa",
        actionWord: "aprobar",
        status: "active",
        confirmButtonText: "Aprobar",
      });
    },
    rejectProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Rechazar programa",
        actionWord: "rechazar",
        status: "rejected",
        confirmButtonText: "Rechazar programa",
      });
    },
    async updateProgramPatientStatus(programPatient, status, payload = {}) {
      try {
        await programPatientService.updateProgramPatientStatus(
          programPatient._id,
          status,
          payload
        );

        const index = this.programPatients.findIndex(
          (p) => p._id === programPatient._id
        );
        if (index !== -1) {
          this.programPatients.splice(index, 1);
        }

        const messages = {
          active: "Programa activado exitosamente",
          rejected: "Programa rechazado",
          cancelled: "Programa cancelado",
        };

        const types = {
          active: "success",
          rejected: "warning",
          cancelled: "warning",
        };

        this.$message({
          type: types[status] || "success",
          message: messages[status] || "Estado actualizado",
        });
      } catch (error) {
        console.error("Error updating program patient status:", error);
        this.$message({
          type: "error",
          message: "Error al actualizar el estado del programa",
        });
      }
    },
    goToPatientDashboard(patient) {
      const patientId = typeof patient === "string" ? patient : patient._id;
      this.$router.push({
        name: "patient-dashboard",
        params: { id: patientId },
      });
    },
    getRemainingFormsTooltip(programPatient) {
      const forms = programPatient.program.initialData;
      if (forms.length <= 1) return "";

      const remainingForms = forms.slice(1).map((form) => form.name);
      return remainingForms.join(", ");
    },
    getStatusType: getProgramStatusType,
    getStatusText: getProgramStatusText,
    openAdditionalInfo(programPatient) {
      this.selectedProgramPatient = programPatient;
      this.showAdditionalInfoModal = true;
    },
    closeAdditionalInfo() {
      this.showAdditionalInfoModal = false;
      this.selectedProgramPatient = null;
    },
    submitAdditionalInfo(payload) {
      if (payload?.context?.programPatient) {
        const index = this.programPatients.findIndex(
          (p) => p._id === this.selectedProgramPatient._id
        );
        if (index !== -1) {
          this.$set(
            this.programPatients[index],
            "additionalInformation",
            payload.context.programPatient.additionalInformation
          );
        }
      }

      this.$message({ type: "success", message: "Solicitud enviada" });
      this.closeAdditionalInfo();
      this.$emit("additional-info-requested", payload);
    },
    askToCompleteAdditionalInformation(programPatient) {
      this.$confirm(
        "¿Ya han respondido la información solicitada?",
        "Completar información adicional",
        {
          confirmButtonText: "Sí",
          cancelButtonText: "No",
          type: "question",
        }
      )
        .then(() => {
          this.completeAdditionalInformation(programPatient);
        })
        .catch(() => {});
    },
    async completeAdditionalInformation(programPatient) {
      try {
        await programPatientService.completeAdditionalInformation(
          programPatient._id
        );

        const index = this.programPatients.findIndex(
          (p) => p._id === programPatient._id
        );
        if (index !== -1) {
          this.$set(this.programPatients[index], "additionalInformation", {
            ...this.programPatients[index].additionalInformation,
            status: "completed",
            statusChangedAt: new Date(),
            statusChangedBy: this.$store.state.user._id,
          });
        }

        this.$message({
          type: "success",
          message: "Información adicional marcada como completada",
        });
      } catch (error) {
        console.error("Error completing additional information:", error);
        this.$message({
          type: "error",
          message: "Error al completar la información adicional",
        });
      }
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(
    navigationType="administration"
    :showNavigation="true"
  )
    template(#title)
      | Administración de Programas

    template(#actions)
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        placeholder="Buscar por paciente o programa"
        :clearable="true"
      )

  .box.box--with-subnav
    .box__content--stretch
      spinner(v-if="isLoading && !programPatients.length")

      h2.no-results(v-if="!isLoading && !programPatients.length") No hay programas de pacientes

      table(v-if="programPatients.length")
        thead
          tr
            th Paciente
            th Programa
            th Estado
            th Formularios
            th Fecha de Creación
            th Acciones
        tbody
          tr(
            v-for="programPatient in filteredProgramPatients"
            :key="programPatient._id"
          )
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.patient.lastName | capitalize }}, {{ programPatient.patient.firstName | capitalize }}
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.program.name }}
            td(@click="goToPatientDashboard(programPatient.patient)")
              el-tag(
                :type="getStatusType(programPatient.status)"
                size="small"
              ) {{ getStatusText(programPatient.status) }}
              span.status-warning-icon(
                v-if="programPatient.additionalInformation && programPatient.additionalInformation.requested && programPatient.additionalInformation.status === 'pending'"
                @click.stop="askToCompleteAdditionalInformation(programPatient)"
                v-tooltip="programPatient.additionalInformation.description || 'Información adicional solicitada'"
              )
                micon(name="info")
              span.status-completed-icon(
                v-if="programPatient.additionalInformation && programPatient.additionalInformation.requested && programPatient.additionalInformation.status === 'completed'"
                v-tooltip="'Información adicional respondida correctamente'"
              )
                micon(name="check")
            td(@click="goToPatientDashboard(programPatient.patient)") 
              | {{ programPatient.program.initialData[0].name }} 
              span.span-more(
                v-if="programPatient.program.initialData.length > 1"
                v-tooltip="getRemainingFormsTooltip(programPatient)"
              ) +{{ programPatient.program.initialData.length - 1 }}
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.createdAt | formatDateAndTime }}
            td.actions
              .actions-container
                button.button.button--action.success(
                  @click.stop="activateProgramPatient(programPatient)"
                  v-tooltip="'Aprobar'"
                )
                  micon(name="check")
                button.button.button--action.danger(
                  @click.stop="rejectProgramPatient(programPatient)"
                  v-tooltip="'Rechazar'"
                )
                  micon(name="clear")
                span(
                  v-tooltip="programPatient.additionalInformation && programPatient.additionalInformation.status === 'pending' ? 'Ya se ha solicitado información' : 'Solicitar información adicional'"
                )
                  button.button.button--action.additional-info(
                    @click.stop="openAdditionalInfo(programPatient)"
                    :disabled="programPatient.additionalInformation && programPatient.additionalInformation.status === 'pending'"
                  )
                    micon(name="info")
  ModalAdditionalInformation(
    :showDialogModal="showAdditionalInfoModal"
    :programPatient="selectedProgramPatient"
    @close="closeAdditionalInfo"
    @submit="submitAdditionalInfo"
  )
</template>

<style lang="scss">
.box--with-subnav {
  min-height: 100px;
}

.no-results {
  text-align: center;
  padding: 50px;
  font-size: 1.5em;
  width: 100%;
}

tbody td:hover {
  cursor: pointer;
}

tbody td.actions {
  cursor: default;
}

.actions-container {
  display: flex;
  gap: 8px;
  align-items: center;

  > span {
    display: inline-flex;
  }
}

.button--action {
  border: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;

  &:disabled {
    background-color: #c0c4cc;
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background-color: #c0c4cc;
    }

    .micon {
      fill: #909399;
    }
  }

  &.success {
    background-color: #67c23a;

    &:hover {
      background-color: #5daf34;
    }

    .micon {
      fill: white;
    }
  }

  &.danger {
    background-color: #f56c6c;

    &:hover {
      background-color: #f45656;
    }

    .micon {
      fill: white;
    }
  }

  &.info {
    background-color: #909399;

    &:hover {
      background-color: #82848a;
    }

    .micon {
      fill: white;
    }
  }

  &.additional-info {
    background-color: #ffb300;

    &:hover {
      background-color: #ffa000;
    }

    .micon {
      fill: white;
    }
  }
}

.span-more {
  color: #fff;
  background-color: #040055;
  margin-left: 5px;
  padding: 6px;
  border-radius: 100%;
  font-size: 12px;
  cursor: pointer;
}

.status-warning-icon .micon {
  margin-left: 6px;
  fill: #e6a23c;
  cursor: pointer;
}

.status-completed-icon .micon {
  margin-left: 6px;
  fill: #67c23a;
}

.complete-info-popover {
  .complete-info-content {
    text-align: center;
    padding: 15px;

    .popover-question {
      margin: 0 0 15px 0;
      font-weight: 500;
      color: #303133;
    }

    .popover-actions {
      display: flex;
      gap: 10px;
      justify-content: center;
    }
  }
}

::v-deep .el-message-box__status.el-icon-success,
::v-deep .el-message-box__status.el-icon-warning {
  display: none;
}

::v-deep .el-message-box__message {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
</style>
